/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import { task } from 'ember-concurrency-decorators';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import type Report from 'embercom/models/reporting/custom/report';
import type Admin from 'embercom/models/admin';
import type Session from 'embercom/services/session';
import type IntlService from 'embercom/services/intl';
import { without, indexBy, omit } from 'underscore';
import { type TaskGenerator } from 'ember-concurrency';
import { cached } from 'tracked-toolbox';
import { taskFor } from 'ember-concurrency-ts';
import type BpoAccessControlService from 'embercom/services/bpo-access-control';

export interface IntercomAvatar {
  isCompany: boolean;
  color: string;
}
export interface IntercomAdmin {
  id?: string;
  name: string;
  avatarData: IntercomAvatar;
}
export interface ViewItem {
  id: string;
  name: string;
  description?: string;
  updatedAt?: Date | null;
  route: string;
  isStaticReport: boolean;
  author?: Admin | IntercomAdmin | null | undefined;
  isNew?: boolean;
  hasReplacement?: boolean;
  updatedBy?: Admin | IntercomAdmin | null | undefined;
  isLegacy?: boolean;
}

export interface FolderReport {
  title: string;
  id: string;
}

export interface ReportItem {
  id: string;
  name: string;
  route?: string;
  isStaticReport?: boolean;
}

export interface ReportingView {
  id: string;
  name: string; // translation key
  icon?: string;
  iconSet?: string;
  svgIcon?: string;
  reports: ViewItem[];
}

export enum ViewId {
  SHARED_REPORT = 'shared-reports',
  MY_REPORTS = 'my-reports',
  INTERCOM_REPORTS = 'intercom-reports',
}

export type ViewIdStrings = `${ViewId}`;

type ReportFilter = (report: ViewItem) => boolean;

const topicReportIds = ['topics', 'suggestions'];

const reportFilters: Record<string, ReportFilter> = {
  excludeNew: (report: ViewItem) => !report.isNew,
  excludeTopic: (report: ViewItem) => !topicReportIds.includes(report.id),
  excludeReplaced: (report: ViewItem) => !report.hasReplacement,
  excludeLegacy: (report: ViewItem) => !report.isLegacy,
};

export default class ReportingViews extends Service {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare session: Session;
  @service declare appService: AppService;
  @service declare permissionsService: any;
  @service declare bpoAccessControl: BpoAccessControlService;

  @tracked reports: Report[] = [];

  @tracked private isSetup = false;

  @task({ drop: true })
  *refreshReports(): TaskGenerator<void> {
    let allReports = yield this.store.findAll('reporting/custom/report', {
      reload: true,
      backgroundReload: false,
    });
    this.reports = allReports.filter((report: Report) => !report.isStaticReport);
  }

  @task({ drop: true })
  *setUp(): TaskGenerator<void> {
    if (!this.isSetup) {
      yield taskFor(this.refreshReports).perform();
      this.isSetup = true;
    }
  }

  shutDown() {
    this.isSetup = false;
  }

  findView(id: string): ReportingView | undefined {
    return this.reportTabs.find((view) => view.id === id);
  }

  get canViewCustomReports() {
    return this.permissionsService.currentAdminCan('can_access_reporting');
  }

  get reportsWithReplacementIds() {
    let reportsWithReplacements = [
      'calls',
      'conversations',
      'conversation-tags',
      'customer-satisfaction',
      'effectiveness',
      'fin',
      'copilot',
      'responsiveness',
      'slas',
      'team-inbox-performance-report',
      'teammate-performance-report',
      'tickets',
      'resolution-bot',
      'team-performance',
    ];

    if (
      this.appService.app.cannotSeeCSOverviewReport ||
      this.appService.app.hasDeprecatedCSOverviewReport
    ) {
      reportsWithReplacements.push('customer-support-overview');
    }

    return reportsWithReplacements;
  }

  @cached
  get allReports(): ViewItem[] {
    let dynamicReports = this.reports
      .sortBy('updatedAt')
      .reverse()
      .map((report) => {
        return {
          id: report.id,
          name: report.title || this.intl.t('reporting.custom-reports.report.untitled'),
          route: 'apps.app.reports.custom-reports.report.show',
          isStaticReport: false,
          updatedAt: report.updatedAt,
          author: report.createdByAdmin,
          isNew: report.get('isNew'),
          updatedBy: report.updatedByAdmin,
          description: report.description,
          isLegacy: false,
        };
      });

    let staticReportNames: Set<string> = new Set(Object.keys(this.staticReports));

    let staticReports = Object.entries(this.staticReports)
      .filter(([k, _]) => staticReportNames.has(k))
      .map(([id, report]) => {
        return {
          id,
          name: report.name,
          route: report.route,
          isStaticReport: true,
          updatedAt: null,
          author: null,
          isNew: false,
          hasReplacement: report.hasReplacement,
          updatedBy: null,
          description: report.description,
          isLegacy: !report.hasReplacement && report.hasReplacement !== undefined,
        };
      });

    return [...staticReports, ...dynamicReports];
  }

  private composeFilters(filters: Array<ReportFilter>): (report: ViewItem) => boolean {
    return (report: ViewItem) => filters.every((filter) => filter(report));
  }

  private applyFilters(reports: ViewItem[], filters: Array<ReportFilter>): ViewItem[] {
    return reports.filter(this.composeFilters(filters));
  }

  get displayableReports(): ViewItem[] {
    let filters: Array<ReportFilter> = [
      reportFilters.excludeNew,
      reportFilters.excludeTopic,
      reportFilters.excludeReplaced,
    ];

    if (this.bpoAccessControl.hasLimitedReportingAccess) {
      filters.push(reportFilters.excludeLegacy);
    }

    return this.applyFilters(this.allReports, filters);
  }

  get standaloneDefaultReport(): Report {
    return this.reports.find(
      (report: Report) => report.createdFromTemplateId === 'fin_standalone',
    )!;
  }

  findBestReportForTemplate(templateId: string): Report | undefined {
    let currentAdminId = this.appService.app.currentAdmin.id;

    return this.reports
      .filter((report: Report) => {
        if (report.createdFromTemplateId !== templateId) {
          return false;
        }
        let isUnedited = !report.updatedById || report.createdById === report.updatedById;
        let isMine = report.createdById === currentAdminId;
        // Exclude reports edited by someone else that aren't ours
        return isUnedited || isMine;
      })
      .sort((a: Report, b: Report) => {
        let aUnedited = !a.updatedById || a.createdById === a.updatedById;
        let bUnedited = !b.updatedById || b.createdById === b.updatedById;
        let aIsMine = a.createdById === currentAdminId;
        let bIsMine = b.createdById === currentAdminId;

        if (aUnedited !== bUnedited) {
          return aUnedited ? -1 : 1;
        }
        if (aIsMine !== bIsMine) {
          return aIsMine ? -1 : 1;
        }
        return 0;
      })[0];
  }

  @cached
  get allReportsById(): Record<string, ViewItem> {
    return indexBy(this.allReports, 'id');
  }

  @cached
  get myReports(): ViewItem[] {
    return this.displayableReports.filter(
      (report) => report.author?.id === this.appService.app.currentAdmin.id,
    );
  }

  @cached
  get sharedReports(): ViewItem[] {
    return this.displayableReports.filter(
      (report) =>
        report.author?.id !== this.appService.app.currentAdmin.id && report.author !== null,
    );
  }

  @cached
  get defaultReports(): ViewItem[] {
    return this.displayableReports.filter(
      (report) => report.author === null || report.isStaticReport,
    );
  }

  get reportTabs(): ReportingView[] {
    return [
      {
        id: 'shared-reports',
        name: 'reporting.submenu-component.items.all-reports',
        icon: 'column-chart',
        iconSet: 'standard',
        reports: this.sharedReports,
      },
      {
        id: 'my-reports',
        name: 'reporting.submenu-component.items.all-reports',
        icon: 'column-chart',
        iconSet: 'standard',
        reports: this.myReports,
      },
      {
        id: 'intercom-reports',
        name: 'reporting.submenu-component.items.all-reports',
        icon: 'column-chart',
        iconSet: 'standard',
        reports: this.defaultReports,
      },
    ];
  }

  get humanSupportReports() {
    let allHumanSupportReports: string[] = [
      'customer-support-overview',
      'calls',
      'conversations',
      'conversation-tags',
      'customer-satisfaction',
      'effectiveness',
      'copilot',
      'responsiveness',
      'slas',
      'team-inbox-performance-report',
      'teammate-performance-report',
      'tickets',
    ];

    let reportsToRemove = [this.appService.app.canUseInboxSlas ? '' : 'slas'];
    if (this.appService.app.cannotSeeCSOverviewReport) {
      reportsToRemove.push('customer-support-overview');
    }

    return without(allHumanSupportReports, ...reportsToRemove);
  }

  get staticViews(): ReportingView[] {
    return [
      {
        id: 'automation',
        name: 'reporting.submenu-component.views.automation',
        icon: 'bounced',
        reports: [
          'conversational-support-funnel',
          'fin',
          'resolution-bot',
          'customer-support-custom-bots',
        ]
          .map((id) => this.staticReports[id])
          .compact(),
      },
      {
        id: 'human-support',
        name: 'reporting.submenu-component.views.human-support',
        reports: this.humanSupportReports.map((id) => this.staticReports[id]).compact(),
      },
      {
        id: 'proactive',
        name: 'reporting.submenu-component.views.proactive',
        reports: ['articles'].map((id) => this.staticReports[id]),
      },
      {
        id: 'conversation-topics',
        name: 'reporting.submenu-component.items.conversational-insights.title',
        reports: this.topicReports,
      },
    ];
  }

  get topicReports(): ViewItem[] {
    return topicReportIds.map((id) => this.staticReports[id]);
  }

  get finReportTitle(): string {
    return this.intl.t(`reporting.submenu-component.items.customer-support.ai-chatbot`);
  }

  get staticReports(): Record<string, ViewItem> {
    let reports = [
      {
        id: 'overview',
        name: this.intl.t(
          'reporting.submenu-component.items.customer-support.intercom-reports-overview',
        ),
        route: 'apps.app.reports.overview',
      },
      {
        id: 'articles',
        name: this.intl.t('reporting.submenu-component.items.customer-support.articles'),
        route: 'apps.app.reports.customer-support.articles.index',
        hasReplacement: false,
      },
      {
        id: 'calls',
        name: this.intl.t('reporting.submenu-component.items.calls'),
        route: 'apps.app.reports.customer-support.calls',
        hasReplacement: true,
      },
      {
        id: 'copilot',
        name: this.intl.t('reporting.submenu-component.items.customer-support.copilot'),
        route: 'apps.app.reports.customer-support.fin-ai-copilot',
        hasReplacement: true,
      },
      {
        id: 'conversations',
        name: this.intl.t('reporting.submenu-component.items.customer-support.conversations'),
        route: 'apps.app.reports.customer-support.conversations-flexible',
        hasReplacement: true,
      },
      {
        id: 'conversational-support-funnel',
        name: this.intl.t(
          'reporting.submenu-component.items.customer-support.conversational-support',
        ),
        route: 'apps.app.reports.customer-support.conversational-support-funnel',
        hasReplacement: false,
      },
      {
        id: 'conversation-tags',
        name: this.intl.t('reporting.submenu-component.items.customer-support.conversation-tags'),
        route: 'apps.app.reports.customer-support.conversation-tags',
        hasReplacement: true,
      },
      this.appService.app.cannotSeeCSOverviewReport ||
      this.appService.app.hasDeprecatedCSOverviewReport
        ? null
        : {
            id: 'customer-support-overview',
            name: this.intl.t('reporting.submenu-component.items.customer-support.title-overview'),
            route: 'apps.app.reports.customer-support.overview',
            hasReplacement: false,
          },
      {
        id: 'customer-satisfaction',
        name: this.intl.t('reporting.submenu-component.items.customer-satisfaction'),
        route: this.csatRoute,
        hasReplacement: true,
      },
      {
        id: 'customer-engagement-overview',
        name: this.intl.t('reporting.submenu-component.items.customer-engagement.title-overview'),
        route: 'apps.app.reports.outbound-engagement.overview',
        hasReplacement: false,
      },
      {
        id: 'customer-support-custom-bots',
        name: this.intl.t('reporting.submenu-component.items.customer-support.custom-bots'),
        route: 'apps.app.reports.customer-support.custom-bots.sent',
        hasReplacement: false,
      },
      {
        id: 'effectiveness',
        name: this.intl.t('reporting.submenu-component.items.customer-support.effectiveness'),
        route: 'apps.app.reports.customer-support.effectiveness',
        hasReplacement: true,
      },
      {
        id: 'email-deliverability',
        name: this.intl.t(
          'reporting.submenu-component.items.customer-engagement.email-deliverability',
        ),
        route: 'apps.app.reports.outbound-engagement.email-deliverability',
        hasReplacement: false,
      },
      {
        id: 'fin',
        name: this.finReportTitle,
        route: 'apps.app.reports.customer-support.fin-ai-agent',
        hasReplacement: true,
      },
      {
        id: 'lead-generation',
        name: this.intl.t('reporting.submenu-component.items.lead-generation.leads'),
        route: 'apps.app.reports.lead-generation.leads',
        hasReplacement: false,
      },
      {
        id: 'lead-generation-custom-bots',
        name: this.intl.t(
          'reporting.submenu-component.items.lead-generation.custom-bots-lead-generation',
        ),
        route: 'apps.app.reports.lead-generation.custom-bots',
        hasReplacement: false,
      },
      {
        id: 'responsiveness',
        name: this.intl.t('reporting.submenu-component.items.responsiveness'),
        route: 'apps.app.reports.customer-support.responsiveness',
        hasReplacement: true,
      },
      {
        id: 'sales',
        name: this.intl.t('reporting.submenu-component.items.lead-generation.sales'),
        route: 'apps.app.reports.lead-generation.sales',
        hasReplacement: false,
      },
      this.appService.app.canUseInboxSlas
        ? {
            id: 'slas',
            name: this.intl.t('reporting.submenu-component.items.customer-support.slas'),
            route: 'apps.app.reports.customer-support.slas',
            hasReplacement: true,
          }
        : null,
      {
        id: 'team-inbox-performance-report',
        name: this.intl.t(
          'reporting.submenu-component.items.customer-support.team-inbox-performance',
        ),
        route: 'apps.app.reports.customer-support.team-inbox-performance-report',
        hasReplacement: true,
      },
      {
        id: 'teammate-performance-report',
        name: this.intl.t(
          'reporting.submenu-component.items.customer-support.teammate-performance',
        ),
        route: 'apps.app.reports.customer-support.teammate-performance-report',
        hasReplacement: true,
      },
      {
        id: 'tickets',
        name: this.intl.t('reporting.submenu-component.items.tickets'),
        route: 'apps.app.reports.customer-support.tickets',
        hasReplacement: true,
      },
      {
        name: this.intl.t('reporting.submenu-component.items.conversational-insights.topics'),
        route: 'apps.app.reports.conversational-insights.topics',
        id: 'topics',
      },
      {
        name: this.intl.t('reporting.submenu-component.items.conversational-insights.suggestions'),
        route: 'apps.app.reports.conversational-insights.suggested-topics',
        id: 'suggestions',
      },
    ]
      .compact()
      .map((report) => {
        return { ...report, isStaticReport: true };
      });

    return indexBy(reports, 'id');
  }

  get csatRoute(): string {
    return 'apps.app.reports.customer-support.customer-satisfaction-v2';
  }

  get staticReportsWithoutTopics(): Record<string, ViewItem> {
    let filteredReports = omit(this.staticReports, topicReportIds);

    return omit(filteredReports, this.reportsWithReplacementIds);
  }
}

declare module '@ember/service' {
  interface Registry {
    reportingViews: ReportingViews;
    'reporting-views': ReportingViews;
  }
}
